import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, Sparkles, MessageSquare } from "lucide-react";
import useSEO from "@/lib/useSEO";
import api from "@/lib/api";

const YELP_URL = "https://m.yelp.com/biz/paulas-cleaning-service-arden-arcade";

function Stars({ rating = 5 }) {
  const r = Math.max(1, Math.min(5, Number(rating) || 5));
  return (
    <div className="flex items-center gap-0.5 text-[#F59E0B]" role="img" aria-label={`${r} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} aria-hidden="true" className={`w-4 h-4 ${i < r ? "fill-current" : "text-slate-200"}`} />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <article
      data-testid={`review-card-${review.id}`}
      className="bg-white rounded-2xl p-6 border border-slate-100 shadow-soft hover:shadow-card transition-shadow text-left"
    >
      <Stars rating={review.rating} />
      <p className="mt-3 text-[#0F172A] leading-relaxed text-sm">
        {review.review_excerpt}
      </p>
      <div className="mt-5 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#475569]">
          {review.customer_display_name}{" "}
          <span className="text-slate-500">· {review.source || "Customer"}</span>
        </p>
        {review.review_date && (
          <span className="text-[11px] text-slate-400 whitespace-nowrap">
            {review.review_date}
          </span>
        )}
      </div>
    </article>
  );
}

export default function Reviews() {
  useSEO({ slug: "reviews", title: "P & A Cleaning Reviews | Sacramento Cleaning Services" });

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/reviews").then((r) => setReviews(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-reviews">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Reviews
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight">
            P &amp; A Cleaning <span className="gradient-text">Reviews</span>
          </h1>
          <p className="mt-4 text-[#475569] text-lg leading-relaxed">
            Real feedback from customers who trusted us with their homes,
            offices, and properties.
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className="pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-sm text-[#475569] py-12" data-testid="reviews-loading">
              Loading reviews…
            </div>
          ) : reviews.length === 0 ? (
            <div
              data-testid="reviews-empty"
              className="rounded-3xl border border-slate-100 bg-[#F8FAFC] p-10 lg:p-14 text-center"
            >
              <div className="w-12 h-12 mx-auto rounded-2xl bg-white border border-slate-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <p className="mt-5 text-lg text-[#0F172A] font-display font-medium max-w-xl mx-auto leading-relaxed">
                Customer reviews will be added soon. Visit our Yelp page to see
                current customer reviews.
              </p>
              <a
                href={YELP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="reviews-empty-yelp-cta"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D32323] text-white font-semibold hover:bg-[#B91C1C] transition-colors shadow-soft"
              >
                View More Reviews on Yelp <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <>
              <div
                data-testid="reviews-grid"
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>

              <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#8B5CF6] text-white p-8 lg:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl font-display font-bold">
                  See more on Yelp
                </h2>
                <p className="mt-2 text-white/85 max-w-xl mx-auto">
                  Read more reviews from customers across Sacramento.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <a
                    href={YELP_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reviews-yelp-cta"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D32323] text-white font-semibold hover:bg-[#B91C1C] transition-colors"
                  >
                    View More Reviews on Yelp <ExternalLink className="w-4 h-4" />
                  </a>
                  <Link
                    to="/request-estimate"
                    data-testid="reviews-cta-estimate"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 transition-colors"
                  >
                    Request an Estimate
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
